import React from 'react';
import { IconButton, Tooltip, useTheme } from '@mui/material';
import {
  DeleteOutlined as RemoveIcon,
  Comment as CommentIcon,
  KeyboardArrowUp as UpIcon,
  KeyboardArrowDown as DownIcon,
} from '@mui/icons-material';
import { CommentMode, RowType } from './types';

interface RecipeRowActionsProps {
  rowIndex: number;
  totalRows: number;
  readonly: boolean;
  canAddRow: boolean;
  groupComments: CommentMode;
  groupRowType: RowType;
  onRemoveRow: (rowIndex: number) => void;
  onMoveRow: (rowIndex: number, direction: 'up' | 'down') => void;
  onToggleComment: (rowIndex: number) => void;
}

const RecipeRowActions: React.FC<RecipeRowActionsProps> = ({
  rowIndex,
  totalRows,
  readonly,
  canAddRow,
  groupComments,
  groupRowType,
  onRemoveRow,
  onMoveRow,
  onToggleComment,
}) => {
  const theme = useTheme();
  const canShowRowActions = totalRows > 1 || groupRowType === RowType.Fileupload;
  const showComments = groupComments !== undefined && groupComments !== null && groupComments !== CommentMode.OFF;
  const showRemoveRow = !readonly && canAddRow && canShowRowActions;
  const showMoveUp = !readonly && canAddRow && canShowRowActions && rowIndex > 0;
  const showMoveDown = !readonly && canAddRow && canShowRowActions && rowIndex < totalRows - 1;

  if (!showComments && !showRemoveRow && !showMoveUp && !showMoveDown) {
    return null;
  }

  return (
    <div className="btn-container">
      <div className="btn-grid-cell">
        {showRemoveRow ? (
          <Tooltip title="Remove Row">
            <IconButton
              size="small"
              onClick={() => onRemoveRow(rowIndex)}
              sx={{
                padding: '2px',
                backgroundColor: theme.palette.error.light,
                '&:hover': { backgroundColor: theme.palette.error.main },
                minWidth: '24px',
                minHeight: '24px',
                width: '24px',
                height: '24px',
              }}
            >
              <RemoveIcon sx={{ fontSize: '16px', color: theme.palette.error.contrastText }} />
            </IconButton>
          </Tooltip>
        ) : null}
      </div>

      <div className="btn-grid-cell">
        {showMoveUp ? (
          <Tooltip title="Move Up">
            <IconButton size="small" onClick={() => onMoveRow(rowIndex, 'up')} className="recipe-action-btn">
              <UpIcon sx={{ fontSize: '16px' }} />
            </IconButton>
          </Tooltip>
        ) : null}
      </div>

      <div className="btn-grid-cell">
        {showComments ? (
          <Tooltip title="Toggle Comment">
            <IconButton size="small" onClick={() => onToggleComment(rowIndex)} className="recipe-action-btn">
              <CommentIcon sx={{ fontSize: '16px' }} />
            </IconButton>
          </Tooltip>
        ) : null}
      </div>

      <div className="btn-grid-cell">
        {showMoveDown ? (
          <Tooltip title="Move Down">
            <IconButton size="small" onClick={() => onMoveRow(rowIndex, 'down')} className="recipe-action-btn">
              <DownIcon sx={{ fontSize: '16px' }} />
            </IconButton>
          </Tooltip>
        ) : null}
      </div>
    </div>
  );
};

export default React.memo(RecipeRowActions);
